import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Card,
  Typography,
  Button,
  Space,
  message,
  Spin,
  Tag,
  DatePicker,
  Row,
  Col,
  Statistic,
  Modal,
  InputNumber,
  Form,
  Divider,
  Empty,
} from "antd";
import type { Dayjs } from 'dayjs';
import {
  SearchOutlined,
  ReloadOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import api, { getDevicesWithFilters, updateDevice } from "../../services/api";
// Import the correct named export from qrcode.react
import { QRCodeSVG } from "qrcode.react";
import "./styles.css";
import axios from 'axios';

const { Title } = Typography;
const { RangePicker } = DatePicker;

// Local Device interface definition
interface Device {
  id: number;
  deviceCode: string;
  deviceName: string;
  data: any;
  isPay: number;
  project: number | { id: number; name: string };
  addTime: string;
  deviceType: number;
  thirdType: string;
  area: number | { id: number; title: string };
  beian: string;
  exData: {
    driverName?: string;
    idcardNumber?: string;
    monitorKey?: string;
    channel?: any[];
    [key: string]: any;
  };
  deviceFactory: string;
  latitude: string;
  longitude: string;
  uid: string | null;
  price: number | null | string;
  isDel: number;
  remark: string | null;
}

// Define API response interface with pagination structure
interface ApiResponseWithItems<T> {
  code: number;
  msg: string;
  data?: {
    items: T[];
    total?: number;
  } | T[];
}

// Local deviceTypeMap definition
const deviceTypeMap: Record<number, string> = {
  0: "塔吊",
  1: "扬尘",
  3: "视频",
  4: "卸料平台",
  5: "施工升降机",
};

// Define types for payment API responses
interface CreatePaymentResponse {
  tradeNo: string;
  qrCodeUrl: string;
  amount: number;
}

interface PaymentStatusResponse {
  status: string;
  paymentData: {
    status: number;
    data: {
      amount: {
        payer_currency: string;
        total: number;
      };
      appid: string;
      mchid: string;
      out_trade_no: string;
      promotion_detail: any[];
      scene_info: {
        device_id: string;
      };
      trade_state: string;
      trade_state_desc: string;
    };
  };
}

const SettlementCenter: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [filteredDevices, setFilteredDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [paymentModalVisible, setPaymentModalVisible] = useState<boolean>(false);
  const [currentDevice, setCurrentDevice] = useState<Device | null>(null);
  const [paymentCompleted, setPaymentCompleted] = useState<boolean>(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Fetch unpaid devices (isPay = 0)
  const fetchUnpaidDevices = async () => {
    try {
      setLoading(true);
      
      // Using the new API endpoint that filters on the server side
      const response = await getDevicesWithFilters({ isPay: 0, limit:1000 }) as { data: ApiResponseWithItems<Device> };
      
      if (response.data.code === 0 && response.data.data) {
        // Check if data has items property or is an array itself
        const responseData = response.data.data;
        const devicesList = 'items' in responseData 
          ? responseData.items as Device[] 
          : responseData as Device[];
            
        // Filter out deleted devices (isDel = 1)
        const unpaidDevices = devicesList.filter((device: Device) => device.isDel === 0);
        setDevices(unpaidDevices);
        setFilteredDevices(unpaidDevices);
        
        // Calculate total amount
        const total = unpaidDevices.reduce((sum: number, device: Device) => {
          const devicePrice = typeof device.price === 'string' 
            ? parseFloat(device.price) 
            : (device.price || 0);
          return sum + devicePrice;
        }, 0);
        setTotalAmount(total);
      } else {
        message.error("获取未支付设备列表失败");
      }
    } catch (error) {
      console.error("Error fetching unpaid devices:", error);
      message.error("获取未支付设备列表时出错");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchUnpaidDevices();
  }, []);
  
  // Filter devices by date range
  const handleDateRangeChange = (dates: any, dateStrings: [string, string] | []) => {
    if (!dates) {
      setDateRange(null);
      setFilteredDevices(devices);
      // Recalculate total when filters are reset
      const total = devices.reduce((sum: number, device: Device) => {
        const devicePrice = typeof device.price === 'string' 
          ? parseFloat(device.price) 
          : (device.price || 0);
        return sum + devicePrice;
      }, 0);
      setTotalAmount(total);
      return;
    }
    
    setDateRange(dates);
    
    // Filter devices by date range
    const filtered = devices.filter(device => {
      const deviceDate = new Date(device.addTime);
      return deviceDate >= dates[0].toDate() && deviceDate <= dates[1].toDate();
    });
    
    setFilteredDevices(filtered);
    
    // Recalculate total
    const total = filtered.reduce((sum: number, device: Device) => {
      const devicePrice = typeof device.price === 'string' 
        ? parseFloat(device.price) 
        : (device.price || 0);
      return sum + devicePrice;
    }, 0);
    setTotalAmount(total);
  };
  
  // Handle payment for a device
  const handlePayment = (device: Device) => {
    setCurrentDevice(device);
    setPaymentModalVisible(true);
    setPaymentCompleted(false);
  };
  
  // Function to create payment and get QR code
  const createPayment = async (deviceId: number, memberId: number): Promise<CreatePaymentResponse> => {
    try {
      const response = await api.post<CreatePaymentResponse>('/payment/wechat/create', { deviceId, memberId });
      return response.data;
    } catch (error) {
      console.error('Error creating payment:', error);
      throw error;
    }
  };

  // Function to check payment status
  const checkPaymentStatus = async (tradeNo: string): Promise<PaymentStatusResponse> => {
    try {
      const response = await api.get<PaymentStatusResponse>(`/payment/wechat/status/${tradeNo}`);
      return response.data;
    } catch (error) {
      console.error('Error checking payment status:', error);
      throw error;
    }
  };
  
  // Update completePayment function
  const completePayment = async () => {
    if (!currentDevice) return;

    try {
      setLoading(true);

      // Create payment and get QR code
      const paymentData: CreatePaymentResponse = await createPayment(currentDevice.id, /* memberId */ 123); // Replace with actual memberId
      const { tradeNo, qrCodeUrl } = paymentData;

      if (!qrCodeUrl) {
        throw new Error('QR Code URL is undefined');
      }

      // Set qrCodeUrl state
      setQrCodeUrl(qrCodeUrl);

      // Show QR code
      setPaymentCompleted(true);

      // Check payment status every 10 seconds
      const intervalId = setInterval(async () => {
        const statusData: PaymentStatusResponse = await checkPaymentStatus(tradeNo);
        
        // Handle both response formats
        if (statusData.status === 'SUCCESS' || 
            (statusData.paymentData && statusData.paymentData.data && statusData.paymentData.data.trade_state === 'SUCCESS')) {
          clearInterval(intervalId);
          intervalRef.current = null;
          
          // Update device payment status in backend
          try {
            await updateDevice(currentDevice.id, { isPay: 1 });
          } catch (updateError) {
            console.error('Error updating device payment status:', updateError);
          }
          
          message.success(`设备 ${currentDevice.deviceName} 支付成功`);
          fetchUnpaidDevices();
          setPaymentModalVisible(false);
        } else {
          // Get trade state description from one of the possible formats
          const tradeStateDesc = statusData.paymentData?.data?.trade_state_desc || '处理中';
          message.info(tradeStateDesc);
        }
      }, 5000);

      intervalRef.current = intervalId;
    } catch (error) {
      console.error('Payment error:', error);
      message.error('支付处理时出错');
    } finally {
      setLoading(false);
    }
  };
  
  // Function to handle modal close
  const handleModalClose = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setPaymentModalVisible(false);
    setPaymentCompleted(false);
    setQrCodeUrl(null);
  };
  
  // Reset all filters
  const handleResetFilters = () => {
    setDateRange(null);
    setFilteredDevices(devices);
    // Recalculate total
    const total = devices.reduce((sum: number, device: Device) => {
      const devicePrice = typeof device.price === 'string' 
        ? parseFloat(device.price) 
        : (device.price || 0);
      return sum + devicePrice;
    }, 0);
    setTotalAmount(total);
  };
  
  // Table columns
  const columns = [
    {
      title: "设备编号/添加时间",
      dataIndex: "deviceCode",
      key: "deviceCode",
      render: (_: any, record: Device) => (
        <div>
          <div>{record.deviceCode}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.addTime ? new Date(record.addTime).toLocaleString('zh-CN', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            }) : '-'}
          </div>
        </div>
      ),
    },
    {
      title: "所属项目/区域",
      dataIndex: "project",
      key: "project",
      render: (_: any, record: Device) => {
        const projectName = typeof record.project === 'object' && record.project !== null
          ? record.project.name
          : "未知项目";
        
        const areaTitle = typeof record.area === 'object' && record.area !== null
          ? record.area.title
          : "未知区域";
        
        return (
          <div>
            <div>{projectName}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{areaTitle}</div>
          </div>
        );
      },
    },
    {
      title: "设备名称",
      dataIndex: "deviceName",
      key: "deviceName",
      render: (_: any, record: Device) => (
        <div>
          <Tag color="blue">{deviceTypeMap[record.deviceType] || "未知"}</Tag>
          {record.deviceName}
        </div>
      ),
    },
    {
      title: "价格 (元)",
      dataIndex: "price",
      key: "price",
      render: (price: number | null | string) => {
        // Convert price to a number if it's a string
        const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
        return (
          <div style={{ color: '#888ddd', fontWeight: 'bold' }}>
            {numericPrice !== null ? `¥${numericPrice.toFixed(2)}` : "-"}
          </div>
        );
      },
    },
    {
      title: "支付状态",
      dataIndex: "isPay",
      key: "isPay",
      render: (isPay: number) => (
        <Tag color={isPay === 0 ? "red" : "green"} style={{ fontWeight: 'bold', padding: '4px 8px' }}>
          {isPay === 0 ? "未支付" : "已支付"}
        </Tag>
      ),
    },
    {
      title: "操作",
      key: "action",
      render: (_: any, record: Device) => (
        <Space size="middle">
          <Button 
            type="primary" 
            icon={<DollarOutlined />} 
            onClick={() => handlePayment(record)}
            disabled={record.isPay === 1}
          >
            支付
          </Button>
        </Space>
      ),
    },
  ];
  
  return (
    <Card className="settlement-center-container">
      <div className="page-header">
        <Title level={4}>结算中心</Title>
        <Space>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchUnpaidDevices}
          >
            刷新
          </Button>
        </Space>
      </div>
      
      <div className="filter-section">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={12}>
            <div className="date-filter">
              <span className="filter-label">日期范围：</span>
              <RangePicker 
                onChange={handleDateRangeChange} 
                value={dateRange}
              />
            </div>
          </Col>
          <Col xs={24} md={4}>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleResetFilters}
            >
              重置筛选
            </Button>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Statistic
                title="未支付总金额"
                value={totalAmount}
                precision={2}
                valueStyle={{ color: '#cf1322' }}
                prefix="¥"
              />
            </Card>
          </Col>
        </Row>
      </div>
      
      <Divider />
      
      <Spin spinning={loading}>
        {filteredDevices.length > 0 ? (
          <Table
            columns={columns}
            dataSource={filteredDevices}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 10 }}
          />
        ) : (
          <Empty description="没有未支付的设备" />
        )}
      </Spin>
      
      {/* Payment Modal */}
      <Modal
        title="设备支付"
        open={paymentModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={400}
      >
        {currentDevice && (
          <div className="payment-modal-content">
            <p><strong>设备名称：</strong> 
              <Tag color="blue" style={{ marginRight: '8px' }}>
                {deviceTypeMap[currentDevice.deviceType] || "未知"}
              </Tag>
              {currentDevice.deviceName}
            </p>
            <p><strong>设备ID：</strong> {currentDevice.deviceCode}</p>
            <p>
              <strong>所属项目/区域：</strong> 
              {typeof currentDevice.project === 'object' && currentDevice.project !== null
                ? currentDevice.project.name
                : "未知项目"} / 
              {typeof currentDevice.area === 'object' && currentDevice.area !== null
                ? currentDevice.area.title
                : "未知区域"}
            </p>
            <p><strong>应付金额：</strong> ¥{typeof currentDevice.price === 'string' 
              ? parseFloat(currentDevice.price).toFixed(2) 
              : currentDevice.price?.toFixed(2) || "-"}</p>
            
            <Divider />
            
            {!paymentCompleted ? (
              <div style={{ textAlign: 'center' }}>
                <Button 
                  type="primary" 
                  onClick={completePayment}
                  loading={loading}
                  style={{ width: '100%' }}
                >
                  微信支付
                </Button>
              </div>
            ) : (
              <div className="qr-code-container">
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <p>请使用微信扫码支付</p>
                  <QRCodeSVG value={qrCodeUrl || ''} size={200} />
                </div>
                <Spin tip="等待支付完成..." spinning={loading} />
              </div>
            )}
          </div>
        )}
      </Modal>
    </Card>
  );
};

export default SettlementCenter; 